// The Copilot conversation for the active scope. Layout-agnostic: the panel
// decides whether it is docked, a sheet or full screen.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { ChevronDown, ExternalLink, Loader2, MessageSquarePlus, RotateCcw, Send, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import type { CopilotMessageView, Citation } from '@shared/copilot/messages';
import { PROVENANCE_LABEL, safeExternalHref } from '@shared/copilot/messages';
import { askAboutLabel, type ScopeInput, type ResolvedScope } from '@shared/copilot/scope';
import type { ApiError } from '@/lib/api';
import { askCopilot, createCopilotThread, getAttention, getCopilotThread, listCopilotThreads, sendCopilotFeedback } from '@/lib/copilot-api';
import { sameScope, useCopilot } from '@/lib/copilot-context';
import { RichText } from './rich-text';
import { CandidateCard, DraftsCard, HandoffCard, PartsBaseSearchCard } from './proposal-cards';

const fmtTime = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function quickActions(scope: ScopeInput): Array<{ label: string; text: string; mode?: 'research' }> {
  if (scope.type === 'rfq_line') return [
    { label: 'Research this part', text: 'Research this part.', mode: 'research' },
    { label: 'Where are we at?', text: 'Where are we at on this line?' },
    { label: 'Draft supplier RFQs', text: 'Find the best sourcing targets for this P/N and draft RFQs.' },
  ];
  if (scope.type === 'rfq') return [
    { label: 'Where are we at?', text: 'Where are we at?' },
    { label: 'What are we waiting on?', text: 'Who are we waiting on, and what should happen next?' },
    { label: 'Hand off', text: 'Prepare a handoff of this RFQ.' },
    { label: 'Draft customer update', text: 'Draft a short update to the customer.' },
  ];
  if (scope.type === 'catalog_issue') return [{ label: 'Research this identifier', text: 'Research this identifier.', mode: 'research' }];
  return [{ label: 'What needs my attention?', text: 'What needs my attention?' }];
}

function SourceList({ citations, open, highlight }: { citations: Citation[]; open: boolean; highlight: string | null }) {
  if (!open || !citations.length) return null;
  return (
    <ol className="mt-2 space-y-1.5 text-xs border-t border-border pt-2">
      {citations.map((c) => {
        const external = c.href && !c.href.startsWith('/') ? safeExternalHref(c.href) : null;
        return (
          <li key={c.id} id={`cite-${c.id}`} className={`flex gap-2 rounded px-1 ${highlight === c.id ? 'bg-primary-muted' : ''}`}>
            <span className="font-semibold text-primary shrink-0 w-5 text-right">{c.id.slice(1)}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1">
                <span className={`pill ${c.provenance === 'confirmed' ? 'pill-success' : c.provenance === 'inference' ? 'pill-warning' : c.provenance === 'public_research' ? 'pill-info' : 'pill-default'}`}>{PROVENANCE_LABEL[c.provenance]}{c.grade ? ` · ${c.grade}` : ''}</span>
                {c.href?.startsWith('/') ? <Link href={c.href} className="text-primary hover:underline break-words min-w-0">{c.label}</Link>
                  : external ? <a href={external} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-words min-w-0 inline-flex items-center gap-0.5">{c.label}<ExternalLink className="w-3 h-3 shrink-0" /></a>
                  : <span className="break-words min-w-0">{c.label}</span>}
                {c.at && <span className="text-foreground-muted">{new Date(c.at).toLocaleDateString()}</span>}
              </div>
              {c.excerpt && <div className="text-foreground-muted line-clamp-3 break-words">“{c.excerpt}”</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function AssistantMessage({ m, scope, onAsk, onRetry }: { m: CopilotMessageView; scope: ResolvedScope; onAsk: (t: string) => void; onRetry?: () => void }) {
  const { getToken } = useAuth();
  const [showSources, setShowSources] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const [hl, setHl] = useState<string | null>(null);
  const [fb, setFb] = useState(m.feedback);
  const known = useMemo(() => new Set(m.citations.map((c) => c.id)), [m.citations]);
  const onCite = (id: string) => { setShowSources(true); setHl(id); setTimeout(() => document.getElementById(`cite-${id}`)?.scrollIntoView({ block: 'nearest' }), 30); };
  const feedback = (v: 'up' | 'down') => { const n = fb === v ? null : v; setFb(n); void sendCopilotFeedback(m.id, n, () => getToken()).catch(() => setFb(fb)); };
  return (
    <div className="text-sm text-foreground min-w-0">
      {m.text ? <RichText text={m.text} handlers={{ onCite, known }} /> : null}
      {m.limitNote && <div className="mt-1.5 text-xs text-warning-foreground bg-warning-muted rounded px-2 py-1 flex items-center gap-2">
        <span className="flex-1">{m.limitNote}</span>
        {onRetry && <button className="inline-flex items-center gap-1 font-medium text-primary shrink-0" onClick={onRetry}><RotateCcw className="w-3.5 h-3.5" />Try again</button>}
      </div>}
      {m.proposals.map((p, i) => {
        if (p.type === 'handoff' && scope.rfqPublicId) return <HandoffCard key={i} proposal={p} messageId={m.id} rfqPublicId={scope.rfqPublicId} />;
        if (p.type === 'email_drafts') return <DraftsCard key={i} proposal={p} messageId={m.id} rfqPublicId={scope.rfqPublicId} lineItemPublicId={scope.lineItemPublicId} />;
        if (p.type === 'relationship_candidate') return <CandidateCard key={i} proposal={p} index={i} messageId={m.id} citations={m.citations} />;
        if (p.type === 'partsbase_search' && scope.rfqPublicId) return <PartsBaseSearchCard key={i} proposal={p} rfqPublicId={scope.rfqPublicId} onAskAbout={onAsk} />;
        return null;
      })}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-muted">
        {m.citations.length > 0 && <button className="inline-flex items-center gap-0.5 hover:text-foreground" onClick={() => setShowSources((v) => !v)} aria-expanded={showSources}>
          {m.citations.length} source{m.citations.length === 1 ? '' : 's'}<ChevronDown className={`w-3 h-3 transition-transform ${showSources ? 'rotate-180' : ''}`} /></button>}
        {m.trace.length > 0 && <button className="hover:text-foreground" onClick={() => setShowTrace((v) => !v)} aria-expanded={showTrace}>Details</button>}
        <span className="ml-auto inline-flex gap-1">
          <button aria-label="Helpful" aria-pressed={fb === 'up'} className={`p-1 rounded hover:bg-surface-muted ${fb === 'up' ? 'text-success' : ''}`} onClick={() => feedback('up')}><ThumbsUp className="w-3.5 h-3.5" /></button>
          <button aria-label="Not helpful" aria-pressed={fb === 'down'} className={`p-1 rounded hover:bg-surface-muted ${fb === 'down' ? 'text-destructive' : ''}`} onClick={() => feedback('down')}><ThumbsDown className="w-3.5 h-3.5" /></button>
        </span>
      </div>
      <SourceList citations={m.citations} open={showSources} highlight={hl} />
      {showTrace && (
        <ul className="mt-2 text-[11px] text-foreground-muted space-y-0.5 border-t border-border pt-2">
          {m.trace.map((t, i) => <li key={i} className={t.ok ? '' : 'text-warning-foreground'}>{t.ok ? '✓' : '•'} {t.summary}{t.ms ? ` (${t.ms} ms)` : ''}</li>)}
          {m.model && <li>Model: {m.model}</li>}
        </ul>
      )}
    </div>
  );
}

function AttentionList() {
  const { getToken } = useAuth();
  const { data } = useQuery({ queryKey: ['copilot', 'attention'], queryFn: () => getAttention(() => getToken()), staleTime: 60_000 });
  const items = data?.items ?? [];
  const mine = items.filter((i) => i.mine);
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-border p-2.5 text-xs space-y-1.5">
      <div className="font-semibold text-foreground">{mine.length ? `Needs you (${mine.length})` : 'Open handoffs'}</div>
      <ul className="space-y-1">
        {(mine.length ? mine : items).slice(0, 8).map((i) => (
          <li key={i.id} className="flex items-center gap-2 min-w-0">
            <Link href={`/rfqs/${i.rfq.publicId}`} className="text-primary hover:underline shrink-0">{i.rfq.ref}</Link>
            <span className="pill pill-warning shrink-0">{i.label}</span>
            {i.note && <span className="truncate text-foreground-muted min-w-0">{i.note}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CopilotConversation({ onClose }: { onClose: () => void }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const { scope, pageScope, setScope, focusLines, pending, clearPending } = useCopilot();
  const scopeKey = scope.type === 'workspace' ? 'workspace' : `${scope.type}:${scope.ref}`;
  const threads = useQuery({ queryKey: ['copilot', 'threads', scopeKey], queryFn: () => listCopilotThreads(scope, () => getToken()), retry: false });
  const [threadId, setThreadId] = useState<string | null>(null);
  useEffect(() => { setThreadId(threads.data?.threads[0]?.id ?? null); }, [scopeKey, threads.data]);
  const thread = useQuery({ queryKey: ['copilot', 'thread', threadId], queryFn: () => getCopilotThread(threadId!, () => getToken()), enabled: !!threadId });
  const resolved = threads.data?.scope;

  const [text, setText] = useState('');
  const [streaming, setStreaming] = useState<{ q: string; text: string; status: string | null } | null>(null);
  const [error, setError] = useState<{ message: string; retry: { text: string; mode: 'auto' | 'research' } } | null>(null);
  const abort = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const messages = thread.data?.messages ?? [];
  // The newest answer, if it failed or stopped early, can be re-asked; the
  // retry is linked to it (copilot_messages.retry_of_id) for later analysis.
  const retryTarget = (() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.status === 'complete') return null;
    const q = [...messages].reverse().find((x) => x.role === 'user');
    return q ? { id: last.id, question: q.text } : null;
  })();
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [messages.length, streaming?.text, streaming?.status]);
  useEffect(() => { inputRef.current?.focus(); }, [scopeKey]);

  async function send(q: string, mode: 'auto' | 'research' = 'auto', retryOf: string | null = null) {
    const question = q.trim();
    if (!question || streaming) return;
    setError(null);
    setText('');
    setStreaming({ q: question, text: '', status: 'Thinking' });
    try {
      let id = threadId;
      if (!id) {
        const t = await createCopilotThread(scope, () => getToken());
        id = t.thread.id;
        setThreadId(id);
        void qc.invalidateQueries({ queryKey: ['copilot', 'threads', scopeKey] });
      }
      abort.current = new AbortController();
      let model: string | null = null;
      try { const m = localStorage.getItem('copilot:model'); model = m && m !== 'auto' ? m : null; } catch { /* storage unavailable */ }
      await askCopilot(id!, { text: question, mode, model, retryOf }, () => getToken(), (e) => {
        if (e.type === 'status') setStreaming((s) => (s ? { ...s, status: e.label } : s));
        if (e.type === 'text') setStreaming((s) => (s ? { ...s, text: s.text + e.delta, status: null } : s));
      }, abort.current.signal);
      await qc.invalidateQueries({ queryKey: ['copilot', 'thread', id] });
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') setError({ message: (e as unknown as ApiError)?.message ?? 'Copilot could not answer.', retry: { text: question, mode } });
      if (threadId) void qc.invalidateQueries({ queryKey: ['copilot', 'thread', threadId] });
    } finally {
      setStreaming(null);
      abort.current = null;
    }
  }

  // A question handed over by a page ("Research this part", "Ask Copilot").
  useEffect(() => {
    if (!pending || threads.isLoading) return;
    if (pending.autoSend) void send(pending.text, pending.mode); else setText(pending.text);
    clearPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending?.nonce, threads.isLoading]);

  const chip = resolved ? askAboutLabel(resolved) : 'Ask APD Copilot';
  const options: Array<{ scope: ScopeInput; label: string }> = [
    ...(pageScope.type !== 'workspace' ? [{ scope: pageScope, label: pageScope.type === 'rfq' ? 'This RFQ' : 'This page' }] : []),
    ...focusLines.map((l) => ({ scope: { type: 'rfq_line' as const, ref: l.publicId }, label: `${l.lineNumber ? `Line ${l.lineNumber} · ` : ''}${l.partNumber}` })),
    { scope: { type: 'workspace' }, label: 'Whole workspace' },
  ];

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-primary-strong">APD Copilot</div>
          <div className="flex items-center gap-1 min-w-0">
            <label className="sr-only" htmlFor="copilot-scope">Context</label>
            <select id="copilot-scope" className="text-sm font-medium bg-transparent truncate max-w-full min-w-0 pr-1 focus:outline-none cursor-pointer"
              value={JSON.stringify(scope)} onChange={(e) => setScope(JSON.parse(e.target.value))} title="Change what Copilot is looking at">
              {!options.some((o) => sameScope(o.scope, scope)) && <option value={JSON.stringify(scope)}>{chip}</option>}
              {options.map((o) => <option key={JSON.stringify(o.scope)} value={JSON.stringify(o.scope)}>{sameScope(o.scope, scope) ? chip : `Ask about ${o.label}`}</option>)}
            </select>
          </div>
        </div>
        <button className="p-1.5 rounded hover:bg-surface-muted text-foreground-muted" aria-label="New conversation" title="New conversation" onClick={() => setThreadId(null)}><MessageSquarePlus className="w-4 h-4" /></button>
        <Link href="/copilot/settings" className="p-1.5 rounded hover:bg-surface-muted text-foreground-muted text-xs" title="Mailboxes and settings">⚙</Link>
        <button className="p-1.5 rounded hover:bg-surface-muted text-foreground-muted" aria-label="Close Copilot" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-3 py-3 space-y-4" aria-live="polite">
        {threads.isError && <div className="text-sm text-destructive">{(threads.error as unknown as ApiError)?.message ?? 'Copilot is unavailable.'}</div>}
        {scope.type === 'workspace' && <AttentionList />}
        {!messages.length && !streaming && !threads.isError && !thread.isLoading && (
          <div className="text-sm text-foreground-muted space-y-1">
            <div className="text-foreground font-medium">{chip}</div>
            <div>Answers come from APD records, linked email and cited sources. Copilot proposes; you decide. It never sends email.</div>
          </div>
        )}
        {messages.map((m) => m.role === 'user'
          ? <div key={m.id} className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap break-words">{m.text}<div className="text-[10px] opacity-75 mt-0.5 text-right">{m.author ? `${m.author} · ` : ''}{fmtTime(m.createdAt)}</div></div></div>
          : <AssistantMessage key={m.id} m={m} scope={resolved ?? thread.data?.thread.scope ?? { type: 'workspace', ref: null, label: '', rfqPublicId: null, rfqRef: null, lineItemPublicId: null, partNumber: null }} onAsk={(t) => void send(t)}
              onRetry={retryTarget?.id === m.id ? () => void send(retryTarget.question, 'auto', m.id) : undefined} />)}
        {streaming && (
          <>
            <div className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap break-words">{streaming.q}</div></div>
            <div className="text-sm min-w-0">
              {streaming.text && <RichText text={streaming.text.replace(/\[C\d+(?:,\s*C\d+)*\]/g, '')} handlers={{ known: new Set() }} />}
              {streaming.status && <div className="flex items-center gap-1.5 text-xs text-foreground-muted"><Loader2 className="w-3.5 h-3.5 animate-spin" />{streaming.status}…</div>}
            </div>
          </>
        )}
        {error && (
          <div className="text-sm rounded-md border border-destructive/40 bg-danger-muted/40 p-2 flex items-start gap-2">
            <span className="flex-1">{error.message}</span>
            <button className="inline-flex items-center gap-1 text-xs font-medium text-primary" onClick={() => void send(error.retry.text, error.retry.mode)}><RotateCcw className="w-3.5 h-3.5" />Retry</button>
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] space-y-2 bg-surface">
        {!messages.length && !streaming && (
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-3 px-3">
            {quickActions(scope).map((a) => (
              <button key={a.label} className="shrink-0 text-xs px-2.5 py-1 rounded-full border border-border hover:bg-surface-muted" onClick={() => void send(a.text, a.mode ?? 'auto')}>{a.label}</button>
            ))}
          </div>
        )}
        <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); void send(text); }}>
          <label htmlFor="copilot-input" className="sr-only">Ask APD Copilot</label>
          <textarea id="copilot-input" ref={inputRef} rows={1} value={text} onChange={(e) => setText(e.target.value)} maxLength={8000}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(text); } }}
            placeholder={streaming ? 'Copilot is answering…' : chip}
            className="flex-1 min-w-0 resize-none max-h-40 text-sm border border-border rounded-xl px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 [field-sizing:content]" />
          {streaming
            ? <button type="button" className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full border border-border" aria-label="Stop" onClick={() => abort.current?.abort()}><X className="w-4 h-4" /></button>
            : <button type="submit" className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40" aria-label="Send" disabled={!text.trim()}><Send className="w-4 h-4" /></button>}
        </form>
      </div>
    </div>
  );
}
